import AppLayout from '@amzn/awsui-components-core/app-layout';
import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import React, { useEffect } from 'react';
import { StarGridDataProvider, useStarGridData } from './context/StarGridDataContext';
import { useStarGridNav } from './hooks/useStarGridNav';
import { StarGridView } from './starGridConstants';
import { CreateReportView } from './views/CreateReportView';
import { DataSyncView } from './views/DataSyncView';
import { InfraSetupView } from './views/InfraSetupView';
import { ReportDetailView } from './views/ReportDetailView';
import { StarGridHomeView } from './views/StarGridHomeView';

/** Side-nav href for the fixed (non-parametric) views. */
const HREF_BY_VIEW: Record<Exclude<StarGridView, 'report'>, string> = {
  home: '#home',
  'create-report': '#create-report',
  infra: '#infra',
  'data-sync': '#data-sync'
};
const VIEW_BY_HREF: Record<string, Exclude<StarGridView, 'report'>> = {
  '#home': 'home',
  '#create-report': 'create-report',
  '#infra': 'infra',
  '#data-sync': 'data-sync'
};

/**
 * Inner shell (inside the data provider) — owns nav state and renders the active sub-view. Split
 * from StarGridPage so the side navigation can read the shared PnL report list (for the dynamic
 * "Reports" section) via context.
 */
const StarGridShell: React.FC = () => {
  const { activeView, activeReportId, setActiveView, openReport, navigationOpen, setNavigationOpen } = useStarGridNav();
  const { pnlReports, toolsContent, toolsOpen, setToolsOpen } = useStarGridData();

  // Load the report list once so the Reports nav section can populate (and hide when empty).
  const { ensureLoaded: ensureReportsLoaded } = pnlReports;
  useEffect(() => {
    ensureReportsLoaded();
  }, [ensureReportsLoaded]);

  const reports = pnlReports.data ?? [];

  const content: React.ReactNode = (() => {
    switch (activeView) {
      case 'create-report':
        return (
          <CreateReportView
            onCreated={(reportId) => {
              pnlReports.refresh();
              openReport(reportId);
            }}
          />
        );
      case 'infra':
        return <InfraSetupView />;
      case 'data-sync':
        return <DataSyncView />;
      case 'report':
        return activeReportId !== undefined ? (
          <ReportDetailView
            reportId={activeReportId}
            onDeleted={() => {
              pnlReports.refresh();
              setActiveView('home');
            }}
            onBack={() => setActiveView('home')}
          />
        ) : (
          <StarGridHomeView onCreate={() => setActiveView('create-report')} onOpenReport={openReport} />
        );
      case 'home':
      default:
        return <StarGridHomeView onCreate={() => setActiveView('create-report')} onOpenReport={openReport} />;
    }
  })();

  const activeHref = activeView === 'report' ? (activeReportId !== undefined ? `#report-${activeReportId}` : '#home') : HREF_BY_VIEW[activeView];

  // Dynamic "Reports" section — one link per created report; omitted entirely when there are none.
  const reportsSection: SideNavigationProps.Item[] =
    reports.length > 0
      ? [
          {
            type: 'section',
            text: 'Reports',
            items: reports.map((report) => ({
              type: 'link',
              text: report.report_name,
              href: `#report-${report.pnl_report_id}`
            }))
          }
        ]
      : [];

  const items: SideNavigationProps.Item[] = [
    {
      type: 'section',
      text: 'Admin',
      items: [
        { type: 'link', text: 'Create Report', href: '#create-report' },
        { type: 'link', text: 'Data Sync', href: '#data-sync' },
        { type: 'link', text: 'Infra Setup details', href: '#infra' }
      ]
    },
    ...reportsSection
  ];

  return (
    <AppLayout
      headerSelector="#h"
      contentType={activeView === 'data-sync' ? 'table' : 'default'}
      maxContentWidth={activeView === 'data-sync' ? Number.MAX_SAFE_INTEGER : undefined}
      toolsHide={!toolsContent}
      tools={toolsContent ?? undefined}
      toolsOpen={toolsOpen}
      onToolsChange={({ detail }) => setToolsOpen(detail.open)}
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      navigation={
        <SideNavigation
          activeHref={activeHref}
          header={{ text: 'StarGrid', href: '#home' }}
          onFollow={(event) => {
            event.preventDefault();
            const href = event.detail.href;
            const reportMatch = /^#report-(\d+)$/.exec(href);
            if (reportMatch) {
              openReport(Number(reportMatch[1]));
              return;
            }
            const next = VIEW_BY_HREF[href];
            if (next) {
              setActiveView(next);
            }
          }}
          items={items}
        />
      }
      content={content}
    />
  );
};

/**
 * StarGrid feature shell — Cloudscape AppLayout with a collapsible side navigation:
 * StarGrid Home, a dynamic "Reports" section listing created reports (hidden when empty), and an
 * "Admin" section (Create Report, Infra Setup). Shared server state lives in StarGridDataProvider;
 * each sub-view owns its own data hooks.
 */
export const StarGridPage: React.FC = () => (
  <StarGridDataProvider>
    <StarGridShell />
  </StarGridDataProvider>
);

export default StarGridPage;
